import { formatUk, weekdayOf } from './calendar.js';
import { dinnerPortions } from './portions.js';
import type {
  ExtraLine, Ingredient, IsoDate, LunchComponent, Meal, PlannedDinner, PlannedLunch, PlannedStaple, Person, Requirement,
  Weekday,
} from './types.js';
import { convert, round2 } from './units.js';

/** UK long form for a weekday code, for error messages naming a person's actual lunch days. */
const WEEKDAY_NAMES: Record<Weekday, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

export interface Lookups {
  ingredients: ReadonlyMap<string, Ingredient>;
  meals: ReadonlyMap<string, Meal>;
  lunchComponents: ReadonlyMap<string, LunchComponent>;
}

/** "2026-08-24" -> "Monday". Source labels are read by people, so they are UK form. */
export function dayName(date: IsoDate): string {
  return formatUk(date).split(' ')[0] ?? date;
}

export function requireIngredient(lookups: Lookups, id: string, usedBy: string): Ingredient {
  const ingredient = lookups.ingredients.get(id);
  if (!ingredient) {
    throw new Error(
      `Unknown ingredient "${id}" referenced by ${usedBy}. Every ingredient must exist in the canonical ` +
      `table — dropping it would make the shopping list quietly short.`,
    );
  }
  return ingredient;
}

export function dinnerRequirements(
  dinners: PlannedDinner[],
  household: number,
  lookups: Lookups,
): Requirement[] {
  const out: Requirement[] = [];
  for (const dinner of dinners) {
    const meal = lookups.meals.get(dinner.mealId);
    if (!meal) {
      throw new Error(`Unknown meal "${dinner.mealId}" planned on ${formatUk(dinner.date)}`);
    }

    if (dinner.portions !== null && (!Number.isFinite(dinner.portions) || dinner.portions <= 0)) {
      throw new Error(
        `"${meal.name}" on ${formatUk(dinner.date)} has an impossible portions override ` +
        `(${dinner.portions}). Use null to mean the whole household.`,
      );
    }
    const portions = dinnerPortions(dinner.portions ?? household, dinner.spansDays);
    const source = `${meal.name} (${dayName(dinner.date)})`;

    for (const line of meal.ingredients) {
      if (line.isOptional) continue;
      const ingredient = requireIngredient(lookups, line.ingredientId, source);
      const perPortion = convert(line.qtyPerPortion, line.unit, ingredient.baseUnit);
      out.push({
        ingredientId: ingredient.id,
        qty: round2(perPortion * portions),
        sources: [source],
      });
    }
  }
  return out;
}

/**
 * A lunch box is one lunch box. The child's portionFactor is deliberately NOT
 * applied — a 2.5-year-old's sandwich is still a whole sandwich. Scaling here
 * would under-buy the entire lunch section.
 */
export function lunchRequirements(
  lunches: PlannedLunch[],
  lookups: Lookups,
  people: Person[],
): Requirement[] {
  const out: Requirement[] = [];
  for (const lunch of lunches) {
    const person = people.find((p) => p.id === lunch.personId);
    if (!person) {
      throw new Error(
        `Unknown person "${lunch.personId}" has a lunch planned on ${formatUk(lunch.date)}. ` +
        `Every lunch must belong to a real member of the household — buying food for nobody ` +
        `is how the list quietly grows.`,
      );
    }

    const day = weekdayOf(lunch.date);
    if (!person.lunchDays.includes(day)) {
      const has = person.lunchDays.length > 0
        ? person.lunchDays.map((d) => WEEKDAY_NAMES[d]).join(', ')
        : 'no days at all';
      throw new Error(
        `${person.name} does not have a packed lunch on ${WEEKDAY_NAMES[day]} — their lunch ` +
        `days are ${has}. Buying a lunch for a day a child is not there is exactly the ` +
        `over-buy this app exists to prevent.`,
      );
    }

    const source = `Lunch — ${person.name} (${dayName(lunch.date)})`;
    for (const componentId of lunch.componentIds) {
      const component = lookups.lunchComponents.get(componentId);
      if (!component) {
        throw new Error(
          `Unknown lunch component "${componentId}" in ${lunch.personId}'s lunch ` +
          `on ${formatUk(lunch.date)}`,
        );
      }
      const ingredient = requireIngredient(lookups, component.ingredientId, `${source} / ${component.name}`);
      out.push({
        ingredientId: ingredient.id,
        qty: convert(component.qtyPerLunch, component.unit, ingredient.baseUnit),
        sources: [source],
      });
    }
  }
  return out;
}

export function stapleRequirements(staples: PlannedStaple[], lookups: Lookups): Requirement[] {
  return staples.map((staple) => {
    const ingredient = requireIngredient(lookups, staple.ingredientId, 'the staples list');
    return {
      ingredientId: ingredient.id,
      qty: convert(staple.qty, staple.unit, ingredient.baseUnit),
      sources: ['Staples'],
    };
  });
}

/**
 * Sum demand per ingredient. This is the step that turns three meals wanting
 * mince into one shopping line — and the reason ingredients are referenced by
 * id rather than by name.
 */
export function aggregateRequirements(reqs: Requirement[]): Requirement[] {
  const byIngredient = new Map<string, { qty: number; sources: string[] }>();

  for (const req of reqs) {
    const existing = byIngredient.get(req.ingredientId);
    if (existing) {
      existing.qty = round2(existing.qty + req.qty);
      for (const source of req.sources) {
        if (!existing.sources.includes(source)) existing.sources.push(source);
      }
    } else {
      byIngredient.set(req.ingredientId, {
        qty: round2(req.qty),
        sources: [...new Set(req.sources)],
      });
    }
  }

  return [...byIngredient].map(([ingredientId, value]) => ({
    ingredientId,
    qty: value.qty,
    sources: value.sources,
  }));
}

const normaliseExtra = (value: string): string => value.toLowerCase().trim().replace(/\s+/g, ' ');

/** One key per label+unit, so "Gochujang"/"gochujang " merge but tbsp and jars never do. */
export function extraKey(label: string, unitLabel: string): string {
  return `extra:${normaliseExtra(label)}|${normaliseExtra(unitLabel)}`;
}

/**
 * Demand for ingredients that are not in the catalogue.
 *
 * Kept apart from `dinnerRequirements` on purpose: those are keyed by
 * catalogue id and pack-rounded, and an uncontrolled label there is exactly
 * the "mince" vs "beef mince" double-buy the catalogue exists to prevent. Two
 * separate gochujang lines are acceptable; a wrong sum is not.
 */
export function dinnerExtras(
  dinners: PlannedDinner[],
  household: number,
  lookups: Lookups,
): ExtraLine[] {
  const byKey = new Map<string, ExtraLine>();
  for (const dinner of dinners) {
    const meal = lookups.meals.get(dinner.mealId);
    if (!meal) {
      throw new Error(`Unknown meal "${dinner.mealId}" planned on ${formatUk(dinner.date)}`);
    }
    const extras = meal.extras ?? [];
    if (extras.length === 0) continue;
    const portions = dinnerPortions(dinner.portions ?? household, dinner.spansDays);
    const source = `${meal.name} (${dayName(dinner.date)})`;

    for (const extra of extras) {
      const key = extraKey(extra.label, extra.unitLabel);
      const qty = extra.qtyPerPortion === null ? null : round2(extra.qtyPerPortion * portions);
      const existing = byKey.get(key);
      if (existing) {
        existing.qty = existing.qty === null ? qty : qty === null ? existing.qty : round2(existing.qty + qty);
        if (!existing.sources.includes(source)) existing.sources.push(source);
      } else {
        byKey.set(key, {
          key, label: extra.label.trim(), qty, unitLabel: extra.unitLabel.trim(),
          aisle: extra.aisle, sources: [source],
        });
      }
    }
  }
  return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
}
